/**
 * First-run onboarding policy: who it is for, and recording that it is done.
 *
 * This module is the ONLY writer of "this org has been through onboarding".
 *
 * Two records, deliberately:
 *  - `organizations.onboarding_completed` (POST /onboarding/complete) is the
 *    source of truth. It is per-organization, so it survives a new device, a
 *    cleared browser, and a second team member joining.
 *  - `onboarding_survey_completed` in localStorage is a local mirror that the
 *    first-run gate reads BEFORE any network call. Its whole job is to make a
 *    redirect loop impossible: if the POST fails (offline, 5xx, expired token)
 *    the user is still let through, and the POST is retried on the next call.
 *
 * Idempotent and safe to call on every mount. The gate is the reader; this file
 * is the writer. Nothing else should touch these keys.
 */

import { apiClient } from '@/lib/api/client';

/* ------------------------------------------------------------------ *
 * Does a subscription prove the org was already set up?
 * ------------------------------------------------------------------ */

/** The `/subscriptions/current` fields this rule reads. */
export interface OnboardingSubscriptionLike {
  id?: string | null;
  status?: string | null;
  /** Stored as a JSON string by TrialService; tolerated as an object too. */
  metadata?: string | Record<string, unknown> | null;
  /** Trial window. Present on the signup trial, absent on a plain paid row. */
  trial_end?: string | Date | null;
  trial_days?: number | string | null;
  /**
   * `slug` identifies the signup trial plan outright; `price` backs it up, since
   * a free plan cannot be evidence of a past purchase.
   */
  plan?: { slug?: string | null; price?: string | number | null } | null;
}

/** `TrialService` stamps every signup trial with this metadata type. */
const SELF_SERVICE_TRIAL_TYPE = 'self_service_trial';

/**
 * `TRIAL_PLAN_SLUG` in `backend/src/application/services/TrialService.js`. The
 * plan every signup trial is created against. A contract across the HTTP
 * boundary — renaming it there means renaming it here.
 */
const TRIAL_PLAN_SLUG = 'trial-15-day';

function readMetadataType(metadata: OnboardingSubscriptionLike['metadata']): string | null {
  if (!metadata) return null;
  let parsed: unknown = metadata;
  if (typeof metadata === 'string') {
    try {
      parsed = JSON.parse(metadata);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const type = (parsed as Record<string, unknown>).type;
  return typeof type === 'string' ? type : null;
}

/**
 * Does this row *look* like a free trial, ignoring `metadata` and `status`?
 *
 * Defence in depth for the metadata check below, which is only as good as the
 * server's column list. `GET /subscriptions/current` has shipped without
 * `metadata` at all (`SubscriptionRepository` selects an explicit field list),
 * and a single dropped column silently turned every new signup into a
 * "returning customer" who skipped onboarding. The row's own shape has to be
 * enough on its own.
 *
 * The plan slug settles it outright when present — the signup trial is always
 * created against `trial-15-day`. Otherwise two conditions, both required:
 *  - a trial window exists (`trial_end`, or a positive `trial_days`), and
 *  - the plan is free.
 *
 * A free plan cannot be evidence of a past purchase, and a converted customer
 * fails the price test even while the original trial dates are still on the row.
 * An unknown or missing price is NOT treated as free — we only claim "trial"
 * when the payload actually says so.
 */
function looksLikeFreeTrial(subscription: OnboardingSubscriptionLike): boolean {
  if (subscription.plan?.slug === TRIAL_PLAN_SLUG) return true;

  const hasTrialWindow =
    subscription.trial_end != null || Number(subscription.trial_days) > 0;
  if (!hasTrialWindow) return false;

  const price = subscription.plan?.price;
  if (price == null || price === '') return false;
  const parsed = Number(price);
  return Number.isFinite(parsed) && parsed === 0;
}

/**
 * Whether having this subscription means the organization has already been
 * through initial setup.
 *
 * THE BUG THIS FIXES. The gate used to treat *any* subscription row as proof,
 * on the reasoning "if they ever had a subscription, even expired, they already
 * went through setup". That premise is false: `AuthService.register` creates a
 * 15-day trial for every single signup (`TrialService.createTrialSubscription`),
 * so a brand-new account always had a subscription with an `id` — and every new
 * user was classified as a returning one and never onboarded. The V1 survey
 * inherited the same flaw, which is why it also never appeared for new signups.
 *
 * A trial created by signup proves only that the user signed up. Anything else —
 * a paid plan, even long cancelled or expired — does mean somebody set this
 * workspace up before, so it still counts.
 *
 * The metadata check is the load-bearing one: it survives the trial later being
 * stamped `expired` by the billing cron, so a lapsed trial is still correctly
 * read as "never onboarded" rather than as prior setup.
 *
 * `status` is deliberately the LAST thing consulted, and never trusted to prove
 * a trial on its own. The API re-derives it via `deriveSubscriptionState`, which
 * reports the signup trial as 'active' rather than 'trialing' — the trial branch
 * there requires no paid period to have started, and `createTrialSubscription`
 * sets `current_period_start`. Anything that keys off `status === 'trialing'`
 * alone is therefore wrong on the real payload.
 */
export function subscriptionProvesPriorSetup(
  subscription: OnboardingSubscriptionLike | null | undefined
): boolean {
  if (!subscription?.id) return false;
  if (readMetadataType(subscription.metadata) === SELF_SERVICE_TRIAL_TYPE) return false;
  if (looksLikeFreeTrial(subscription)) return false;

  const status = (subscription.status || '').toString().toLowerCase();
  // 'none' is the synthetic row returned when there is no subscription at all.
  if (status === 'trialing' || status === 'none') return false;

  return true;
}

/* ------------------------------------------------------------------ *
 * Who first-run onboarding is for
 * ------------------------------------------------------------------ */

/**
 * The only user fields the audience rule reads. Deliberately structural rather
 * than the full `User` type, so the rule can be unit-tested against plain
 * objects instead of a fully-populated session.
 */
export interface FirstRunOnboardingUser {
  role?: string | null;
  is_agency?: boolean | null;
  is_super_admin?: boolean | null;
  is_organization_owner?: boolean | null;
}

/**
 * True only for a new customer setting up their own workspace.
 *
 * Excluded, each for a different reason:
 *  - **WeNext platform admins** (`is_super_admin`). Staff reach /home only when
 *    inspecting a client workspace; redirecting them into that client's channel
 *    setup is both wrong and destructive — they would be completing someone
 *    else's onboarding. This is never a business first-run.
 *  - **Agency users** (`is_agency`). They have no tenant workspace to set up and
 *    live under /home/agency/*.
 *  - **Invited members** (role USER / AGENT, not owner). Their organization may
 *    legitimately sit at `onboarding_completed = false`, and connecting a
 *    WhatsApp number is not theirs to do.
 *
 * Included: the workspace owner. Both account-creation paths — self-signup
 * (`AuthService.register`, which `registerWithPhone` delegates to) and
 * admin-created clients (`ClientService`) — produce `role: 'admin'` with
 * `is_organization_owner: true` and `is_super_admin: false`, so this predicate
 * matches exactly the person whose account it is.
 *
 * Eligibility is necessary but NOT sufficient: the caller still requires the
 * backend to say this organization has not completed onboarding. This answers
 * "is this the right person?", never "is this the right time?".
 */
export function isFirstRunOnboardingAudience(
  user: FirstRunOnboardingUser | null | undefined
): boolean {
  if (!user) return false;
  if (user.is_super_admin) return false;
  if (user.is_agency) return false;

  const role = (user.role || '').toString().toUpperCase();
  return role === 'ADMIN' || user.is_organization_owner === true;
}

/**
 * Local mirror of `organizations.onboarding_completed`.
 *
 * The VALUE is the organization id, not `'true'`.
 *
 * Onboarding is per-ORGANIZATION but localStorage is per-BROWSER, so a bare
 * boolean cannot say *whose* onboarding finished. A browser that completed
 * onboarding for one workspace was therefore short-circuiting the gate for the
 * next account signed into it — before it ever asked the server — and the
 * no-business-account branch then sent that brand-new org to the setup plan,
 * which marks onboarding complete on mount. One irreversible write later, the
 * questions were unreachable for a workspace that had never seen them. That is
 * exactly the "I created a new account and it still sends me to
 * /onboarding/dashboard" report.
 *
 * `resetOnboardingCompletionForNewAccount()` only covers the in-app signup
 * paths; an admin-created client, or signing into a second workspace, never
 * passes through it.
 */
export const ONBOARDING_COMPLETED_KEY = 'onboarding_survey_completed';
/** Pre-org-scoping value. Deliberately NOT trusted — see `mirrorMatches`. */
const LEGACY_MIRROR_VALUE = 'true';
/** Set when the local mirror is ahead of the server, so we retry the POST. */
export const ONBOARDING_COMPLETE_PENDING_KEY = 'onboarding_complete_pending';
/** Set at signup, consumed once onboarding finishes. */
const NEW_SIGNUP_KEY = 'new_signup';
/**
 * Dead key from the removed NEXA first-signup auto-open. Nothing writes it any
 * more; it is cleared on completion so browsers that still carry it from an
 * earlier signup do not keep a flag whose meaning no longer exists.
 */
const NEXA_INTRO_PENDING_KEY = 'nexa_widget_intro_pending';

/**
 * Every localStorage access is wrapped: private-mode Safari and hardened
 * profiles throw on access, and onboarding must not break there.
 */
function readKey(key: string): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // Best effort. The server record is the real one.
  }
}

function removeKey(key: string): void {
  try {
    window.localStorage?.removeItem(key);
  } catch {
    // Best effort.
  }
}

/**
 * Does the stored mirror belong to THIS organization?
 *
 * A legacy `'true'` is honoured only when the caller cannot name an
 * organization. Once we know which workspace is asking, an unattributable value
 * is treated as a MISS: the cost of being wrong is one extra
 * `/onboarding/status` request, which immediately re-caches an org-scoped
 * value, while the cost of wrongly trusting it is a real customer who never
 * sees first-run setup.
 */
function mirrorMatches(raw: string | null, organizationId?: string | null): boolean {
  if (!raw) return false;
  if (!organizationId) return raw === LEGACY_MIRROR_VALUE;
  return raw === organizationId;
}

/**
 * True when THIS organization has been recorded locally as onboarded.
 *
 * Always pass the current `user.organization_id`. Omitting it falls back to the
 * legacy browser-wide behaviour, which cannot tell two workspaces apart.
 */
export function isOnboardingCompletedLocally(organizationId?: string | null): boolean {
  return mirrorMatches(readKey(ONBOARDING_COMPLETED_KEY), organizationId);
}

/** True when the local mirror was set but the server POST has not landed yet. */
export function isOnboardingCompletePending(): boolean {
  return readKey(ONBOARDING_COMPLETE_PENDING_KEY) === 'true';
}

/**
 * True for a session that just created its account and has not finished
 * onboarding yet.
 *
 * The signup flow writes this the moment the account exists, and
 * `markOnboardingComplete` / `clearTokens` clear it. The first-run gate uses it
 * to skip the "was this workspace already set up?" heuristic entirely: someone
 * who signed up seconds ago cannot have prior setup, so every answer that
 * heuristic could give is either irrelevant or wrong — and its failure modes all
 * resolve to "silently skip onboarding for the whole session". The gate still
 * asks the server whether onboarding is done, so this can never cause a loop.
 */
export function isNewSignupPending(): boolean {
  return readKey(NEW_SIGNUP_KEY) === 'true';
}

/**
 * Stamp this browser session as a brand-new signup.
 *
 * Written the moment the account exists (phone or email register). The Auth
 * page and the first-run gate both read it so a fresh user is sent to
 * `/onboarding` instead of Overview — without waiting on a network round-trip
 * that can silently skip them.
 */
export function markNewSignupPending(): void {
  writeKey(NEW_SIGNUP_KEY, 'true');
}

/**
 * Forget any onboarding state left in this browser, for a freshly created
 * account.
 *
 * The mirror is per-BROWSER while onboarding is per-ORGANIZATION, so without
 * this a device that ever finished onboarding would skip it for every later
 * signup on the same device — the gate short-circuits on the local mirror before
 * it ever asks the server. That hits exactly the people most likely to sign up
 * twice on one machine: whoever is testing the flow, and anyone creating a
 * second workspace.
 *
 * `clearTokens()` already clears these on logout; this covers signing up without
 * an intervening logout.
 */
export function resetOnboardingCompletionForNewAccount(): void {
  removeKey(ONBOARDING_COMPLETED_KEY);
  removeKey(ONBOARDING_COMPLETE_PENDING_KEY);
}

/**
 * Mirror a completion the server has already confirmed.
 *
 * Used by the gate when `/onboarding/status` (or an existing subscription) shows
 * the org is past onboarding. No POST — the server is already correct, so any
 * stale pending marker is cleared too.
 */
export function cacheOnboardingCompleted(organizationId?: string | null): void {
  writeKey(ONBOARDING_COMPLETED_KEY, organizationId || LEGACY_MIRROR_VALUE);
  removeKey(ONBOARDING_COMPLETE_PENDING_KEY);
}

/**
 * Record that this organization has been through first-run onboarding.
 *
 * Order matters: the local mirror is written FIRST so a failed request can
 * never trap the user in the onboarding redirect. The server POST follows and,
 * if it fails, leaves a pending marker for the next call to retry.
 *
 * Call this when the user reaches the setup plan — whether they answered every
 * question or skipped. "Completed" here means "has been shown the flow", which
 * is exactly what the V1 survey gate meant by it too.
 */
export async function markOnboardingComplete(organizationId?: string | null): Promise<void> {
  const alreadyComplete = isOnboardingCompletedLocally(organizationId);
  const needsRetry = isOnboardingCompletePending();

  // Nothing to do: already recorded locally and the server agrees.
  if (alreadyComplete && !needsRetry) return;

  writeKey(ONBOARDING_COMPLETED_KEY, organizationId || LEGACY_MIRROR_VALUE);
  removeKey(NEW_SIGNUP_KEY);
  // Stale from the removed NEXA auto-open. Cleared here rather than left behind
  // so no browser is still carrying a "please open the assistant" flag.
  removeKey(NEXA_INTRO_PENDING_KEY);

  // NOTHING is announced here. This used to dispatch `nexa:intro-open`, which
  // popped the NEXA assistant open the moment a first-time user reached the
  // setup plan — see the comment in ChatWidget for why that is gone.

  try {
    await apiClient.post('/onboarding/complete');
    removeKey(ONBOARDING_COMPLETE_PENDING_KEY);
  } catch {
    // The user is already through locally. Mark it for retry so the server
    // record catches up on the next visit rather than re-running onboarding
    // on their next device.
    writeKey(ONBOARDING_COMPLETE_PENDING_KEY, 'true');
  }
}
